import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";

import { organizationIdTail, type OrganizationCellView } from "../utils/organizationCell";

export interface OrganizationCellProps {
  view: OrganizationCellView;
  /** The catalog preload is still running — every cell resolves together, so this is column-wide. */
  isDirectoryPending: boolean;
  /** This one id is being fetched by the per-id fallback. Only these resolve independently. */
  isIdPending: boolean;
}

/**
 * The organization a person belongs to, in four states that must stay distinguishable.
 *
 * **`none` and `unknown` are opposite claims** — "belongs to no organization" versus "we could not find out
 * which" — so they get different words AND different treatments. Rendering both as an em-dash is the bug this
 * component exists to prevent.
 *
 * Loading is column-wide for the preload (all cells flip together from one request, so twenty-five
 * independently timed skeletons would misrepresent one request as twenty-five) and per-cell only for the
 * fallback, where cells genuinely do resolve one at a time.
 */
export function OrganizationCell({ view, isDirectoryPending, isIdPending }: OrganizationCellProps) {
  const { t } = useTranslations("users");

  if (view.kind === "unknown" && (isDirectoryPending || isIdPending)) {
    return <ColocaSkeleton className="h-4 w-28" />;
  }

  if (view.kind === "none") {
    // Affirmative. The column is NOT NULL server-side, so this is a data defect and must read as one.
    return <span className="text-sm text-destructive">{t("org_none")}</span>;
  }

  if (view.kind === "unknown") {
    return (
      <span title={view.id} className="truncate font-geist-mono text-xs italic text-muted-foreground">
        …{organizationIdTail(view.id)}
        <span className="ml-1 not-italic">({t("org_unresolved")})</span>
      </span>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span
        title={view.name}
        className={
          view.kind === "root" ? "truncate text-sm text-muted-foreground" : "truncate text-sm text-foreground"
        }
      >
        {view.name}
      </span>
      {view.kind === "root" && <span className="text-xs text-muted-foreground">{t("org_internal")}</span>}
    </div>
  );
}
